'use client'

import { Box, UnstyledButton } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useTranslations } from 'next-intl'
import { useEffect, useMemo, useState } from 'react'

import UnionIcon from '@/assets/icons/union.svg'
import type { PredictionNavNode } from '@/services/prediction/types'

import NavChipList, { type NavChipListItem } from '../NavChipList'
import { isAllNode, isOnActivePath, sortByOrdering } from '../NavTree/utils'
import styles from './styles.module.css'
import { SubcategorySheet } from './SubcategorySheet'

/** 给 chip 条开头的 floating "more" 按钮(navButton)预留的左侧内嵌空间。 */
const NAV_BUTTON_LEADING_INSET = 46

/** 格式化 node 的 event_count 为 chip 角标文本;无有用值时返回 undefined。 */
function formatEventCount(count: number | undefined): string | undefined {
  if (typeof count !== 'number' || count <= 0) return undefined
  return String(count)
}

interface SecondLevelNavTreeProps {
  /** 当前选中的 L1 根节点——决定 L2 条与 sheet 渲染谁的子分类。 */
  rootNode: PredictionNavNode
  /** 当前选择路径（`childNode?.path ?? rootNode.path`），驱动高亮。 */
  selectedPath: string
  /** 选中 L2 节点（含 All = rootNode 本身）——即 PredictionSection 的 handleTreeSelect。 */
  onSelect: (node: PredictionNavNode) => void
}

/**
 * L2 子分类 chip 条 + 底部抽屉入口,`SecondLevelNavTree`(og-web)的 cdc port
 * (cdc-web hub/predict 同款)。只渲染在移动端(<midScale 992px,与桌面左树
 * 严格互补)。
 *
 * 读取与 L1 同一棵导航树:当前激活 L1 root 的 folder 子节点渲染成 pill chip
 * 横向条(复用 NavChipList `mobileVariant="pill"`),条左缘 pinned 一个悬浮
 * "more" 按钮,点击打开 SubcategorySheet(移动端完整子分类列表)。
 *
 * cdc-market adaptations from cdc-web:
 * - No URL sync: 激活 root / 高亮从 `rootNode` + `selectedPath`(本地 selection
 *   state)推导,而不是从 pathname 反解;chip 点击走上层 handleTreeSelect。
 * - Only folder-type L1s 有子分类(sports/crypto);destination 型 L1
 *   (Trending / Live / Climate)没有,返回 null。
 */
export default function SecondLevelNavTree({
  rootNode,
  selectedPath,
  onSelect,
}: Readonly<SecondLevelNavTreeProps>) {
  const t = useTranslations('HomePageV2.Prediction')
  const [sheetOpened, { open: openSheet, close: closeSheet }] = useDisclosure(false)
  // 抽屉里选中 L2 收起后,让 L2 chip 条把高亮 chip 滚到左侧可见区
  // (subcategory 往往离当前 chip 条视口很远,不滚用户看不到选中在哪)。
  const [activeScrollTrigger, setActiveScrollTrigger] = useState(0)

  // L2 folder 子节点,按 BE ordering 排(og 仅渲染 folder 型 + 有 path 的节点)。
  const l2Folders = useMemo<PredictionNavNode[]>(() => {
    if (!rootNode?.children) return []
    return sortByOrdering(
      rootNode.children.filter(child => child.node_type === 'folder' && !!child.path),
    )
  }, [rootNode])

  // 做选择即关 sheet(cdc-web 是路由变化自动关——本地 state 版由选择动作
  // 承担同一语义)。只依赖 rootNode.path:切 L1 时 sheet 若开着会盖住新页,
  // 必须跟着关。
  useEffect(() => {
    closeSheet()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootNode.path])

  const items = useMemo<NavChipListItem[]>(() => {
    if (!rootNode?.path) return []

    const anySubActive = l2Folders.some(
      folder => folder.path && isOnActivePath(folder, selectedPath),
    )

    // All chip 的角标总数:优先 L1 root 自身 count,fallback 到其 All 子节点
    // (og 用共享 isAllNode 识别,而非裸 slug === 'all' —— BE 会为部分 folder
    // 下发 display_name 为 "All" 的 child)。
    const allChildCount = rootNode.children?.find(isAllNode)?.event_count
    const allCount = rootNode.event_count ?? allChildCount

    const allItem: NavChipListItem = {
      slug: `${rootNode.slug}-all`,
      label: t('all'),
      isActive: !anySubActive,
      countLabel: formatEventCount(allCount),
      onClick: () => onSelect(rootNode),
    }

    const folderItems: NavChipListItem[] = l2Folders.map(folder => ({
      slug: folder.slug,
      label: folder.display_name,
      isActive: folder.path ? isOnActivePath(folder, selectedPath) : false,
      countLabel: formatEventCount(folder.event_count),
      iconUrl: folder.icon_url || undefined,
      onClick: () => onSelect(folder),
    }))

    return [allItem, ...folderItems]
  }, [rootNode, l2Folders, selectedPath, t, onSelect])

  // 仅当激活 L1 是 `folder` 型才渲染:destination L1(Trending/Live/Climate)
  // 没有子分类。folder L1 即使只有一个 All destination(Politics/Economics/
  // Culture)也渲染 L2 条 + dropdown(og 同款)。
  if (!rootNode?.path || rootNode.node_type !== 'folder') return null

  return (
    <Box hiddenFrom="midScale" className={styles.l2Strip}>
      <Box className={styles.chipRowWrapper}>
        <NavChipList
          items={items}
          showAllChips
          mobileVariant="pill"
          mobileLeadingInset={NAV_BUTTON_LEADING_INSET}
          // 每个 L1 root 的 L2 条按 root 各自记忆:整页加载首滚一次,root 内
          // 子分类软切换时保持停留位置、不重新滚动(CDCW-8610)。
          scrollTrackKey={`predict-l2-${rootNode.path}`}
          // sheet 里选择后收起 → 把高亮 chip 滚到左侧可见区。
          scrollToActiveTrigger={activeScrollTrigger}
        />

        {/* 悬浮 "more" 入口,pinned 在 chip 条左缘并加渐变淡出,让它和 chip
            同一行而不是下面的独立控件。点击打开同一个 SubcategorySheet。 */}
        <Box className={styles.navButtonAnchor}>
          <Box className={styles.navButtonFade} aria-hidden />
          <UnstyledButton
            className={styles.navButton}
            onClick={openSheet}
            aria-expanded={sheetOpened}
            aria-haspopup="dialog"
            aria-label={t('more')}
          >
            <UnionIcon width={8} height={12} aria-hidden />
          </UnstyledButton>
        </Box>
      </Box>

      <SubcategorySheet
        opened={sheetOpened}
        onClose={closeSheet}
        activeRoot={rootNode}
        selectedPath={selectedPath}
        onSelect={node => {
          onSelect(node)
          setActiveScrollTrigger(v => v + 1)
        }}
      />
    </Box>
  )
}
